// Archive a BOM or routing version (spec 01 §3.3.5 Archive; D-261 amended
// 2026-09-08) — the one dialog both the BOM editor (S3) and the routing
// editor (S4) open. `draft → archived` abandons a draft; `superseded →
// archived` is housekeeping. Never deletes, never undone — which is why it is
// a dialog and not a bare button.
//
// What it says before the human commits: which version goes to the archive
// and what that means for its status (`leadDraft` / `leadSuperseded`), and —
// for a superseded version whose range has not ended by this screen's
// calendar — that the server will REFUSE (VERSION_STILL_RESUMABLE), because
// that version is exactly what withdrawing its scheduled successor would
// bring back. Said up front (`archive-resumable-preview`), the control kept:
// the calendars can disagree at a day boundary and the server decides.
//
// The two §3.3.5 archive refusals each get their own sentence
// (`archiveRefusalOf`): VERSION_STILL_RESUMABLE names the day the row's own
// range ends (`effective_to`, read from the problem's TOP-LEVEL members — the
// wire spreads extensions beside `title`, there is no envelope — and falling
// back to the row in hand, then to a dateless sentence, never a blank) and
// tells the planner to withdraw the scheduled successor first;
// `*_NOT_ARCHIVABLE` says the status moved before the write and names what
// it is now. A refusal is a stable fact about the row: Archive is withdrawn
// from the footer and Close is the only control left, the hook has already
// refetched the row, and the next open starts clean.
//
// Built on `Modal`: no exit while the archive is in flight (the dialog's own
// `onCancel` is a no-op while pending, and the Modal's `pending` makes
// Escape, the scrim and the ✕ inert) — the same guard, for the same reason,
// as the withdraw dialog beside it.

import { enumLabel } from "../../../../lib/enum-label";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Modal } from "../../../../components/modal";
import { Banner } from "../../../../components/toast";
import { problemTitle } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import {
  archiveRefusalOf,
  refusalEffectiveToOf,
  refusalStatusOf,
  refusalTodayOf,
  type ArchivePreview,
} from "./version-schedule";

export interface ArchiveDialogProps {
  /** The version being archived. */
  version: number;
  /** What archiving it means — captured by the caller when the dialog OPENED,
   * so a refetch landing mid-dialog cannot rewrite the sentence under the
   * decision (or unmount the dialog while its refusal is on screen). */
  preview: ArchivePreview;
  /** The row's own `effective_to`: the fallback date for the resumable
   * refusal where the server named none. */
  effectiveTo: string | null;
  pending: boolean;
  /** The archive mutation's error, if any — mapped to a specific sentence. */
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string | undefined;
}

export function ArchiveDialog({
  version,
  preview,
  effectiveTo,
  pending,
  error,
  onConfirm,
  onCancel,
  testId,
}: ArchiveDialogProps) {
  const { t } = useTranslation();

  const refusal = error === null || error === undefined ? null : archiveRefusalOf(error);
  const requestId = requestIdOf(error);
  const serverToday = refusalTodayOf(error);
  const resumableUntil = refusalEffectiveToOf(error) ?? effectiveTo;
  const movedTo = refusalStatusOf(error);
  const errorText =
    error === null || error === undefined
      ? null
      : refusal === "stillResumable"
        ? resumableUntil !== null
          ? t("catalogue.archive.refusal.stillResumable", { date: resumableUntil })
          : t("catalogue.archive.refusal.stillResumableNoDate")
        : refusal === "notArchivable"
          ? movedTo !== undefined
            ? t("catalogue.archive.refusal.notArchivable", {
                status: enumLabel(t, "catalogue.status", movedTo),
              })
            : t("catalogue.archive.refusal.notArchivableNoStatus")
          : (problemTitle(error) ?? t("catalogue.archive.failed"));
  // A refusal will refuse again: the only honest control left is Close.
  const refused = refusal !== null;

  // No exit while the request is in flight — see the header.
  const cancel = () => {
    if (pending) return;
    onCancel();
  };

  return (
    <Modal
      heading={t("catalogue.archive.title")}
      label={t("catalogue.archive.title")}
      closeLabel={t("catalogue.archive.close")}
      onClose={cancel}
      pending={pending}
      className="cat-archive-modal"
      testId={testId ?? "archive-dialog"}
    >
      <div className="cat-archive-body">
        <p className="cat-archive-lead" data-testid="archive-lead" data-archive={preview.kind}>
          {preview.kind === "abandon"
            ? t("catalogue.archive.leadDraft", { version })
            : t("catalogue.archive.leadSuperseded", { version })}
        </p>

        {/* Said before the press, and only while the press is still offered:
         * once refused, the refusal below is the one fact on screen. */}
        {preview.kind === "stillResumable" && !refused ? (
          <Banner kind="warn" data-testid="archive-resumable-preview">
            {t("catalogue.archive.resumablePreview", { date: preview.until })}
          </Banner>
        ) : null}

        {errorText !== null ? (
          <Banner kind="bad" data-testid="archive-error" data-refusal={refusal ?? "other"}>
            {errorText}
            {serverToday !== undefined && refusal === "stillResumable" ? (
              <> {t("catalogue.withdraw.refusal.serverToday", { today: serverToday })}</>
            ) : null}
            {requestId !== undefined ? (
              <span className="rte-request-id">
                {" "}
                {t("catalogue.state.requestId")}: <code>{requestId}</code>
              </span>
            ) : null}
          </Banner>
        ) : null}
      </div>

      <div className="cat-archive-foot">
        <Button onClick={cancel} disabled={pending} data-testid="archive-dismiss">
          {refused ? t("catalogue.archive.close") : t("catalogue.archive.cancel")}
        </Button>
        {refused ? null : (
          <Button
            variant="danger"
            disabled={pending}
            busy={pending}
            busyLabel={t("catalogue.archive.archiving")}
            data-testid="archive-confirm"
            onClick={onConfirm}
          >
            {t("catalogue.archive.confirm")}
          </Button>
        )}
      </div>
    </Modal>
  );
}
